// @givmo/app-charity — what a claimant should EXPECT to happen after they submit (CPA-DP1).
//
// The claim wizard used to say one thing to everybody: "If your email domain matches this
// organization, your claim is verified instantly." Two problems, and the second is the serious one:
//
//   1. It made the reader do the comparison. The app knows both halves — the signed-in address and the
//      organization's website on record — and told the reader to check for themselves.
//   2. It is FALSE in the takeover case. A claim on an organization somebody already manages is ALWAYS
//      routed to review, domain match or not (Charity Portal plan §3.5). A caller who matched the
//      domain, read "verified instantly", and then landed on a document-upload screen was told
//      something untrue by the last screen before they committed.
//
// So the expectation is computed, personal, and hedged: it names the two things being compared, says
// what to EXPECT rather than what will happen, and never states the instant path without its condition.
// THE BACKEND REMAINS AUTHORITATIVE — nothing here changes an outcome, and no claim semantics are
// touched. This module only decides which honest sentence to show before the submit.
//
// Since D-CP-92 (CPA-WEB91) that condition is a super-admin's vetting of the organization, which this
// app cannot see, and the instant path is the exception: the copy no longer calls it usual, and no
// longer promises a turnaround before the claim is in.

export type ExpectationKind = 'match' | 'mismatch' | 'no-domain' | 'unknown-email';

export interface ClaimExpectation {
  kind: ExpectationKind;
  /** The signed-in address, or null when the session has no email (a rare provider edge). */
  email: string | null;
  /** The organization's website on record, normalized, or null when none is on file. */
  domain: string | null;
}

/**
 * Normalize a website-on-record value for comparison: lowercase, trim, drop a scheme, a leading
 * `www.`, and anything after the host. Tolerant by design — `organization_domain` is free-ish text on
 * the wire and has been seen as a bare host, a `www.` host, and a full URL.
 */
export function normalizeDomain(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim().toLowerCase();
  if (trimmed.length === 0) return null;
  const withoutScheme = trimmed.replace(/^[a-z][a-z0-9+.-]*:\/\//, '');
  const host = withoutScheme.split('/')[0]?.split('?')[0]?.split('#')[0] ?? '';
  const withoutWww = host.replace(/^www\./, '');
  return withoutWww.length > 0 ? withoutWww : null;
}

/** The domain part of an email address, normalized the same way, or null. */
export function emailDomain(email: string | null | undefined): string | null {
  if (typeof email !== 'string') return null;
  const at = email.lastIndexOf('@');
  if (at < 0) return null;
  return normalizeDomain(email.slice(at + 1));
}

/**
 * Which expectation applies to this claimant + this organization.
 *
 * `match` is deliberately NOT "you will be verified instantly" — see {@link expectationSentences}.
 */
export function claimExpectation(
  email: string | null | undefined,
  organizationDomain: string | null | undefined,
): ClaimExpectation {
  const normalizedEmail =
    typeof email === 'string' && email.trim().length > 0 ? email.trim() : null;
  const domain = normalizeDomain(organizationDomain);
  if (normalizedEmail === null) return { kind: 'unknown-email', email: null, domain };
  if (domain === null) return { kind: 'no-domain', email: normalizedEmail, domain: null };
  return {
    kind: domainsMatch(emailDomain(normalizedEmail), domain) ? 'match' : 'mismatch',
    email: normalizedEmail,
    domain,
  };
}

/**
 * Does a sender's domain belong to the organization's domain?
 *
 * Exact host OR a DOT-ANCHORED subdomain, because the backend matches on eTLD+1 and a large nonprofit
 * routinely mails from a subdomain — `jane@mail.redcross.org` at `redcross.org`. Comparing exact hosts
 * told her to go and find "a work email at redcross.org", which is the address she was already using.
 *
 * The dot anchor is the whole safety property: `evil-redcross.org` does NOT end with `.redcross.org`,
 * so a look-alike registration cannot borrow the suffix. Direction of the remaining error stays
 * under-promise-safe — a domain this says does not match may still be verified instantly by the
 * backend, which is a pleasant surprise; the reverse would be the false promise B3 exists to prevent.
 */
function domainsMatch(senderDomain: string | null, organizationDomain: string): boolean {
  if (senderDomain === null) return false;
  return senderDomain === organizationDomain || senderDomain.endsWith(`.${organizationDomain}`);
}

/** What a Tier-2 review asks for, named so nobody is surprised by the ask on the next screen. */
export const REVIEW_DOCUMENTS = ['IRS determination letter'] as const;

/**
 * The expectation, as sentences. Returned split so the screen can weight the lead differently from
 * the qualification without either being re-authored at the call site.
 *
 * Since D-CP-92 the instant path runs only for an organization a super-admin has vetted — its
 * website allowed for instant claims — so a matching address alone verifies no one, and this app
 * cannot see which organizations are vetted (CPA-WEB91). Where the address could take that path
 * (`match`), the detail says how every claim is verified, which is true of any charity. Where it
 * cannot, it says a reviewer will check the document: `mismatch`, `no-domain`, and `unknown-email`,
 * because the engine's domain check needs an address and an account with none can never pass it. One
 * sentence each, promising neither the instant path, nor an outcome, nor a turnaround.
 */
export function expectationSentences(expectation: ClaimExpectation): {
  lead: string;
  detail: string;
} {
  const documents = `your ${REVIEW_DOCUMENTS[0]}`;
  const everyClaim =
    "We verify every claim — right away for some organizations we've already vetted, otherwise " +
    `by a reviewer who checks ${documents}.`;
  const reviewer = `Expect a reviewer to check ${documents}.`;
  switch (expectation.kind) {
    case 'match':
      return {
        lead: `You're signed in as ${expectation.email}, which matches ${expectation.domain}.`,
        detail: everyClaim,
      };
    case 'mismatch':
      return {
        lead: `You're signed in as ${expectation.email}, which doesn't match ${expectation.domain}.`,
        detail: reviewer,
      };
    case 'no-domain':
      return {
        lead: `You're signed in as ${expectation.email}. This organization has no website on record.`,
        detail: reviewer,
      };
    case 'unknown-email':
    default:
      return {
        lead: "There's no email address on your account.",
        detail: reviewer,
      };
  }
}

/**
 * The primary button's WORD for this fork (CPA-WEB47, Jonathan 2026-09-07: "if my email domain doesn't
 * match the charity I selected to claim, the blue button should say Next instead of Submit Claim").
 *
 * The wizard already tells a `mismatch` / `no-domain` claimant, two blocks above the button, that a
 * reviewer will ask them for a document — so "Submit claim" promises a finish line that is not there.
 * Where the claim does go to review, the next screen is the claim-detail page and its document upload
 * (`claim-detail-page.tsx`), and that is what "Next" points at.
 *
 * ⚠ NOT UNIVERSAL, and the earlier "the upload screen is literally what comes next" overstated it:
 * {@link domainsMatch} deliberately UNDER-promises, so a `mismatch` the BACKEND verifies anyway returns
 * AUTO_VERIFIED and ends on a terminal panel in place, under a button that said "Next". That is the
 * safe direction of the error — a pleasant surprise rather than a broken promise — and it is the fork
 * Jonathan ruled on, so the word stands.
 *
 * `match` keeps "Submit claim" because the auto-verify fork really can end here. `unknown-email` says
 * "Next" (CPA-WEB91): an account with no email address can never take the instant path, so its copy
 * discloses a review just as `mismatch`'s does, and the word follows the sentence.
 *
 * ⚠ THE WORD ONLY. This changes no behaviour and no precondition: the attestation still gates the
 * button, the same POST still fires, and the backend remains authoritative over the outcome.
 */
export function expectationSubmitLabel(kind: ExpectationKind): string {
  return kind === 'match' ? 'Submit claim' : 'Next';
}
